import { useState, useEffect, useRef } from 'react';
import { formatPrice, formatNumber, formatStreamingValue, normalizeStreamingPrice } from "src/MarketDataComponents/Helpers";
import { NotYetCustomer, MarketHoursDelayInfo, Quote } from 'src/Components/Molecules/Card';
import { isStreamingEnabledForInstrument } from 'src/Utils';
import axios from 'axios';
import YouInvest from 'src/Api/YouInvest';
import moment from 'moment-timezone';

const LOCAL_STORAGE_STREAMING_ENABLED_KEY = 'LIVE_STREAMING';

const msgpack = require('msgpack5')(), decode = msgpack.decode;

export default function InstrumentMain({quote, instrument, marketOpen, settings}) {
    const isLocalStorageEnabled = (key) => {
        if (typeof window !== "undefined") {
            return (localStorage.getItem(key) && localStorage.getItem(key) === "true") ? true : false;
        }

        return false;
    }

    const [searchValue, setSearchvalue] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchResults, setSearchResults] = useState(null);
    const [isGuest, setIsGuest] = useState(null);
    const [streamingEnabled, setStreamingEnabled] = useState(isStreamingEnabledForInstrument(quote) && isLocalStorageEnabled(LOCAL_STORAGE_STREAMING_ENABLED_KEY) ? true : false);
    const [streamingInstrument, setStreamingInstrument] = useState(null);
    const [details, setDetails] = useState(null);

    const webSocket = useRef(null);

    useEffect(() => {
        axios({
            method: 'GET',
            url: `/api/user/details`,
            headers: YouInvest.getTradingRequestHeaders()
		}).then((response) => {
            setDetails(response.data.data);
            setIsGuest(false);
            
            if (streamingEnabled && !response.data.data.electiveProfessional) {
                subscribeStreamingInstruments();
            }
		}).catch((error) => {
            setIsGuest(true);
		})
    }, []);

    useEffect(() => {
        return () => {
            if (webSocket && webSocket.current) {
                webSocket.current.close();
                webSocket.current = null;
            }
            unsubscribeLastStreaming();
        }
    }, []);

    const unsubscribeLastStreaming = () => {
        let toUnsubscribe = null;
        if (streamingInstrument) {
            toUnsubscribe = streamingInstrument.marketCode;
        }
        if (!toUnsubscribe) {
            return;
        }

        if (webSocket.current) {
            webSocket.current.send(`UNSUB ${toUnsubscribe}`);
        }
    }
    
    const subscribeStreamingInstruments = () => {
        if (isStreamingEnabledForInstrument(quote)) {
            setStreamingInstrument(quote);

            const subscribe = () => {           
                if (webSocket.current) {
                    webSocket.current.send(`SUB ${quote.marketCode}`);
                }
            };

            if (!webSocket.current) {
                initStreamingSession().then((streamingHost) => {
                    initStreamingWebSocket(streamingHost, subscribe);			              
                }).catch((err) => {
                    console.log(err);
                });
            } else {
                subscribe();
            }
        }
    }

    const streamingSwitcherEvent = (e) => {
        const value = e.target.checked;

        if (value) {
            subscribeStreamingInstruments();
        } else {
            if (webSocket && webSocket.current) {
                webSocket.current.close();
                webSocket.current = null;
            }
        }

        localStorage.setItem(LOCAL_STORAGE_STREAMING_ENABLED_KEY, value);
        setStreamingEnabled(value);
    }

    const initStreamingSession = () => {
        return new Promise(async (resolve, reject) => {
            try {
                const initResponse = await axios.get(`/api/settings/init-streaming`, {
                    withCredentials: true,
                    headers:  YouInvest.getTradingRequestHeaders()
                });

                if(!initResponse.data.success){
                    Error("Creating streaming session failed");
                }
				
                const streamingHost = initResponse.data.data.endpoint
                const sessionToken = initResponse.data.data.token

                await axios.get(`https://${streamingHost}/session/create?sessionId=${sessionToken}`, {
                    withCredentials: true
                });

                resolve(streamingHost)
            } catch (e) {
				console.log(e);
				setTimeout(() => {
					setStreamingEnabled(false)
                }, 1000);
                reject(e);
            }
        });
	}

    const initStreamingWebSocket = (streamingHost, callback) => {
		
		const priceLastUpdatedTimeEl = jQuery('#priceLastUpdatedTime');

        webSocket.current = new WebSocket(`wss://${streamingHost}/ws`);    
        webSocket.current.binaryType = 'arraybuffer';
		
        webSocket.current.onopen = open = () => {
            if (callback) {
                callback();
            }   
        };

        webSocket.current.onclose = close = () => {
			setStreamingEnabled(false);
		};

		webSocket.current.onmessage = (event) => {
			try { 
				if (event.data[0] === '@') {
						
				} else {                    
					const item = decode(new Uint8Array(event.data));

					const sellObj =  jQuery(`#quoteSellValue`);
					const buyObj =  jQuery(`#quoteBuyValue`);
					const changeObj =  jQuery(`#quoteChangeValues`);
					const dayChangePercentage = formatNumber(item[11], 2, false, '');
                    const dayChangeNormalized = normalizeStreamingPrice(item[10], quote.currency, item[22]);
                    const dayChange = formatNumber(dayChangeNormalized, 2, false, '');
                    const bidPrice = formatStreamingValue(quote.bidPrice, quote.currency, item[3], item[22]);
                    const askPrice = formatStreamingValue(quote.bidPrice, quote.currency, item[4], item[22]);
					
					try { 
						// We need to be careful here and find out if the timezone is UTC or not.
						const newUpdatedDate = moment.utc(item[5], "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY HH:mm:ss")
						priceLastUpdatedTimeEl.html(newUpdatedDate);
					} catch (e) {}
					
					sellObj.html(bidPrice);
					buyObj.html(askPrice);
					changeObj.html(`${dayChange ? dayChange.replace('-','') : '0'} (${dayChangePercentage}%)`);
				}
			} catch (e) {
				// Sentry.captureException(e); // @todo: revisit, too many errors in sentry due to Cannot read properties of undefined (reading 'split')
			}
		}
	}

    const searchOnChange = (e) => {
        setSearchvalue(e.target.value);
    }

    useEffect(() => {
        clearTimeout(window.ajbellSearchTimeout);
        if (!searchValue || searchValue.length < 3) {
            setSearchResults(null);
            return;
        }
        window.ajbellSearchTimeout = setTimeout(() => {
            getSearchResults();
        }, 500);
    }, [searchValue]);

    const getSearchPreview = (e) => {
        if (e.key === 'Enter') {
            submitSearch();
        }
    }

    const getSearchResults = () => {
        setSearchLoading(true);
        axios({
            method: 'GET',
            url: `/api/securities/search?search=${searchValue}&limit=10`,
            headers: YouInvest.getTradingRequestHeaders()
        }).then(response => {
            setSearchLoading(false);
            const results = response.data.data.results;

            if (results.length === 0) {
                setSearchResults(null);
                return;
            }

            setSearchResults(results);
         }).catch(error => {
            setSearchLoading(false);
            setSearchResults(null);
            console.error(error);
        });
    }

    const renderSearchResults = () => {
        const results = searchResults.map((item) => {
           return <li key={item.id}><a href={`/market-research/${item.marketCode}`}>{item.searchDescription}</a></li>           
        });

        return <ul className="search-container z-depth-1">
            {results}
        </ul>
    }

    const submitSearch = (e) => {
        if (e) {
            e.preventDefault();
        }
        window.location = `/search/shares/${encodeURI(searchValue)}`;
    }

    const getBuyOnClick = () => {
        if (instrument && instrument.tradable.buyable) {
            return () => {
                window.location.href = `/platform/trade?marketCode=${quote.marketCode}&instruction=B`;
            }
        }
    }

    const getSellOnClick = () => {   
        if (instrument && instrument.tradable.sellable) {
            return () => {
                window.location.href = `/platform/trade?marketCode=${quote.marketCode}&instruction=S`;
            }
        }        
    }

    const getRegularOnClick = () => {
        if (instrument && instrument.tradable.ri) {
            return () => {
            window.location.href = `/platform/regularinvestments?marketCode=${quote.marketCode}&instruction=R`;
            }
        }
    }
    
    const [quoteObj] = useState({...quote, 
        askPrice: quote.askPrice ? formatPrice(quote.currency, quote.askPrice) : undefined,
        bidPrice: quote.bidPrice ? formatPrice(quote.currency, quote.bidPrice) : undefined,
        dayChange: quote.dayChange ? quote.dayChange.toFixed(2) : 0,
        lastPrice: quote.lastPrice ? formatPrice(quote.currency, quote.lastPrice) : undefined,
        dayChangePct: quote.dayChangePct ? quote.dayChangePct.toFixed(2) : 0
    })

    return <>
        <div className="ajb-grid-row"> {/* grid row class */}
            <div className="ajb-col-collapse"> {/* grid margin collapse class */}
                <div className="ajb-col-8 ajb-col-8-breakpoint"> {/* Column 8 of 12 Desktop : Column 8 of 8 Responsive */}
                    <MarketHoursDelayInfo quote={quote} marketOpen={marketOpen} streamingEnabled={streamingEnabled} streamingSwitcherEvent={streamingSwitcherEvent} settings={settings} isGuest={isGuest} details={details} />
                </div>
                <div className="ajb-col-4 ajb-col-8-breakpoint margin-top-20-sm padding-left-10-sm padding-right-0-sm securities-search"> {/* Column 4 of 12 Desktop : Column 8 of 8 Responsive */}
                    {/* Instrument search form element */}
                    <div className="ajb-grid-row">
                        <div className="ajb-grid-row">
                            <div className="input-field ajb-col-9 ajb-col-5-breakpoint margin-top-0 margin-left-0">
                                <i className="fas fa-search prefix" />
                                <input 
                                    className="margin-bottom-10 margin-bottom-20-sm" 
                                    id="icon_prefix" 
                                    type="text" 
                                    placeholder="Search investments" 
                                    onChange={searchOnChange}
                                    onKeyUp={getSearchPreview}
                                    data-testid="searchInvestmentsInput"
                                />
                                {searchLoading &&
                                <i class="fas fa-circle-notch fa-spin search-loading"></i>}
                            </div>
                            <span className="input-group-btn ajb-col-3 ajb-col-3-breakpoint padding-top-10">
                                <input className="form-submit c-btn c-btn-smaller button-default waves-effect waves-light button button--red" type="submit" value="Search" onClick={submitSearch} data-testid="formSubmitInput" />
                            </span>
                        </div>
                        {searchResults ? renderSearchResults() : null}
                    </div>
                </div>
            </div>
        </div>
        {/* grid row : End */}

        <hr />

        {/* grid row : Start */}
        <div className="ab-grid-row clearfix margin-bottom-20"> {/* grid row class */}
            <div className="ajb-col-collapse"> {/* grid margin collapse class */}

                <div className="ajb-col-8 ajb-col-8-breakpoint margin-bottom-0"> {/* Column 8 of 12 Desktop : Column 8 of 8 Responsive */}
                    <Quote 
                        quote={quoteObj} 
                        handlers={{
                            buyOnClick: getBuyOnClick(),
                            sellOnClick: getSellOnClick(),
                            regularOnClick: getRegularOnClick(),
                        }}                        
                        streamingEnabled={streamingEnabled}
                        setStreamingEnabled={setStreamingEnabled}
                    />
                </div>

                {isGuest ? <NotYetCustomer /> : null}

            </div>
        </div>
    </>

  }